import type { ReactNode, RefObject } from 'react'
import { View } from 'react-native'
import Animated, { type FlatListPropsWithLayout } from 'react-native-reanimated'

import type { TGetCommunityApiData } from '@/src/features/community/api/getCommunity.types'
import type { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'

import { COMMUNITY_LIFETIME } from '../../../models/community.constants'
import type { useCommunitySubscriptionsQuery } from '../../../services/useCommunitySubscriptionsQuery'
import type { usePublicationsOfCommunityQuery } from '../../../services/usePublicationsOfCommunityQuery'
import { useAutoScroll } from '../../hooks/useAutoScroll'
import { PublicationPlaceholders } from '../../screens/CommunityScreen/PublicationsPlaceholders'
import { CommunityEmptyMessage } from '../CommunityEmptyMessage/CommunityEmptyMessage'
import { CommunityPublicationItem } from '../CommunityPublicationItem'

type TPublicationListItem = NonNullable<ReturnType<typeof usePublicationsOfCommunityQuery>['data']>[number]
export type TPublicationsListProps = {
  publications?: TPublicationListItem[]
  isPending: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: () => void
} & Omit<
  FlatListPropsWithLayout<TPublicationListItem>,
  'data' | 'renderItem' | 'keyExtractor' | 'ListFooterComponent' | 'onEndReached' | 'onScrollToIndexFailed'
> & {
    listRef: RefObject<Animated.FlatList | null>
    community: TGetCommunityApiData | undefined
    subscription: ReturnType<typeof useCommunitySubscriptionsQuery>['activeSubscription']
    /** Publication highlighted as the one to pray, defaults to the subscription next publication */
    featuredPublicationId?: number
    /** Index of the publication to scroll to once loaded, -1 to disable */
    autoScrollIndex?: number
    trackingSource?: TRACKING_SOURCE
  }

export const PublicationsList = ({
  publications,
  isPending,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  listRef,
  community,
  subscription,
  featuredPublicationId,
  autoScrollIndex = -1,
  trackingSource,
  ...otherProps
}: TPublicationsListProps): ReactNode => {
  useAutoScroll(listRef, autoScrollIndex, isPending)

  const ListFooter = () =>
    isFetchingNextPage || isPending ? (
      <View className="mt-4">
        <PublicationPlaceholders />
      </View>
    ) : (
      <View className="h-24" />
    )

  const ListEmptyComponent = () =>
    community &&
    publications &&
    publications.length === 0 && (
      <CommunityEmptyMessage
        isEverlasting={community.lifetime === COMMUNITY_LIFETIME.EVERLASTING}
        lifetime={community.lifetime}
      />
    )

  return (
    <Animated.FlatList
      ref={listRef}
      data={publications || []}
      keyExtractor={(item) => item.id.toString()}
      indicatorStyle="white"
      onEndReachedThreshold={0.5}
      ItemSeparatorComponent={() => <></>}
      ListFooterComponent={ListFooter}
      scrollEventThrottle={16}
      onEndReached={() => !isFetchingNextPage && hasNextPage && fetchNextPage()}
      onScrollToIndexFailed={({ index, averageItemLength }) => {
        listRef.current?.scrollToOffset({ offset: averageItemLength * index, animated: true })
      }}
      ListEmptyComponent={ListEmptyComponent}
      renderItem={({ item }) =>
        !community ? null : (
          <CommunityPublicationItem
            community={community}
            publication={item}
            subscription={subscription}
            featuredPublicationId={featuredPublicationId}
            trackingSource={trackingSource}
          />
        )
      }
      {...otherProps}
    />
  )
}
